import { format, formatDistanceToNow, isValid, parseISO, subMonths, subDays, subHours } from 'date-fns'

// ─── Date Format Constants ───────────────────────────────────────────────────
/** Standard date format: "15 Jan 2024" */
export const DATE_FORMAT = 'd MMM yyyy'

/** Standard date+time format: "15 Jan 2024 14:30" */
export const DATE_TIME_FORMAT = 'd MMM yyyy HH:mm'

/** Short date format (no year): "15 Jan" */
export const DATE_FORMAT_SHORT = 'd MMM'

/** Full month date format: "15 January 2024" */
export const DATE_FORMAT_LONG = 'd MMMM yyyy'

/** Compact date+time for timelines/audit: "15 Jan 14:30" */
export const DATE_TIME_FORMAT_SHORT = 'd MMM HH:mm'

// ─── Types ───────────────────────────────────────────────────────────────────
type DateInput = Date | string | number | null | undefined

// ─── Internal Helper ─────────────────────────────────────────────────────────
function toDate(value: DateInput): Date | null {
  if (value == null) return null
  if (value instanceof Date) return isValid(value) ? value : null
  if (typeof value === 'number') {
    const d = new Date(value)
    return isValid(d) ? d : null
  }
  const d = parseISO(value)
  return isValid(d) ? d : null
}

// ─── Display Formatting Functions ────────────────────────────────────────────

/**
 * Format as "15 Jan 2024 14:30" (full date + time).
 * Primary display format for timestamps.
 */
export function formatDate(date: DateInput): string {
  const d = toDate(date)
  if (!d) return 'No Date'
  return format(d, DATE_TIME_FORMAT)
}

/**
 * Format as "15 Jan 2024" (date only, no time).
 */
export function formatDateOnly(date: DateInput): string {
  const d = toDate(date)
  if (!d) return 'No Date'
  return format(d, DATE_FORMAT)
}

/**
 * Format as "15 Jan" (short, no year).
 */
export function formatDateShort(date: DateInput): string {
  const d = toDate(date)
  if (!d) return '-'
  return format(d, DATE_FORMAT_SHORT)
}

/**
 * Format as "15 January 2024" (full month name).
 */
export function formatDateLong(date: DateInput): string {
  const d = toDate(date)
  if (!d) return 'No Date'
  return format(d, DATE_FORMAT_LONG)
}

/**
 * Format as "15 Jan 14:30" (compact date+time for timelines/audit).
 */
export function formatDateTimeShort(date: DateInput): string {
  const d = toDate(date)
  if (!d) return 'No Date'
  return format(d, DATE_TIME_FORMAT_SHORT)
}

/**
 * Format as relative time: "about 3 days ago".
 * Uses date-fns formatDistanceToNow.
 */
export function formatRelativeTime(date: DateInput): string {
  const d = toDate(date)
  if (!d) return 'Unknown'
  return formatDistanceToNow(d, { addSuffix: true })
}

/**
 * Format date with relative time suffix: "15 Jan 2024 (about 3 days ago)".
 */
export function formatDateWithRelative(date: DateInput): string {
  const d = toDate(date)
  if (!d) return 'Unknown'
  return `${format(d, DATE_FORMAT)} (${formatDistanceToNow(d, { addSuffix: true })})`
}

// ─── API / Query Formatting ──────────────────────────────────────────────────

/**
 * Format a date object to YYYYMMDD format for API queries
 *
 * @param date - Date object or ISO string to format
 * @returns Formatted date string in YYYYMMDD format
 * @throws Error if date is invalid
 */
export const formatDateForQuery = (date: Date | string): string => {
  if (!date) {
    throw new Error('Date is required for formatting');
  }

  const d = date instanceof Date ? date : new Date(date);

  if (isNaN(d.getTime())) {
    throw new Error('Invalid date provided for formatting');
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}${month}${day}`;
}

/**
 * Parse a date string in YYYYMMDD format to a Date object
 *
 * @param dateStr - Date string in YYYYMMDD format
 * @returns Date object
 * @throws Error if dateStr is invalid format
 */
export const parseDateFromQuery = (dateStr: string): Date => {
  if (typeof dateStr !== 'string' || dateStr.length !== 8) {
    throw new Error('Date string must be in YYYYMMDD format');
  }

  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);

  // Validate numeric values
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);

  if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum) ||
      monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
    throw new Error('Invalid date values in YYYYMMDD string');
  }

  const d = new Date(`${year}-${month}-${day}`);

  // Validate that the date was created successfully
  if (isNaN(d.getTime())) {
    throw new Error('Invalid date created from YYYYMMDD string');
  }

  return d;
}

/**
 * Get a relative date based on a preset
 *
 * @param preset - Relative date preset (e.g., 'today', 'yesterday', 'last_7d')
 * @returns Date object
 */
export const getRelativeDate = (preset: string): Date | null => {
  const now = new Date();

  switch (preset) {
    case 'today':
      return now;

    case 'yesterday': {
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      return yesterday;
    }

    case 'this_month': {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      return firstDay;
    }

    default:
      if (preset.startsWith('last_')) {
        const value = parseInt(preset.substring(5, preset.length - 1), 10);
        const unit = preset.charAt(preset.length - 1);

        let result: Date;

        switch (unit) {
          case 'h': // hours
            result = subHours(now, value);
            break;
          case 'd': // days
            result = subDays(now, value);
            break;
          case 'm': // months
            result = subMonths(now, value);
            break;
          default:
            return null;
        }

        return result;
      }

      return null;
  }
}
